import { useRef, useState } from "react";
import { Product, SubCategory } from "../../types/products";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  EditProductFormData,
  editProductSchema,
  UpdateFormData,
  updateProductSchema,
} from "../../schemas/edit-product.schema";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/form/form";
import { Textarea } from "src/components/input/TextArea";
import { Input } from "./Input";
import { toast } from "react-toastify";
import ImageGalleryModal from "./ImageGalleryModal";
import { joinByComma } from "src/utils";
import { IMAGE_CDN } from "src/redux/api/appConst";
import { uploadProductThumbnail } from "../../api/product.api";
import { useUpdateProduct } from "../../hooks/products/useUpdateProduct";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/input/Select";

const mapPartialToUpdateDTO = (
  data: Partial<EditProductFormData>,
): Partial<UpdateFormData> => {
  const result: Partial<UpdateFormData> = {};

  if (data.name !== undefined) {
    result.name = data.name;
  }

  if (data.price !== undefined) {
    result.price = Number(data.price);
  }

  if (data.category !== undefined) {
    result.category = data.category;
  }

  if (data.sub_category !== undefined) {
    result.sub_category = data.sub_category;
  }

  if (data.description !== undefined) {
    result.description = data.description;
  }

  if (data.benefits !== undefined) {
    result.benefits = data.benefits
      .split(",")
      .map((b) => b.trim())
      .filter(Boolean);
  }

  if (data.thumbnail !== undefined) {
    result.thumbnail = data.thumbnail;
  }

  return result;
};

const EditProductForm = ({
  product,
  close,
}: {
  product: Product;
  close: () => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string>();
  const [upload, setUpload] = useState<File | null>(null);
  const [galleryKey, setGalleryKey] = useState<string | null>(null);

  const { mutate: updateProductMutation, isPending } = useUpdateProduct();

  const form = useForm<EditProductFormData>({
    resolver: zodResolver(editProductSchema),
    defaultValues: {
      name: product.name,
      price: String(product.price),
      category: product.category,
      sub_category: product.sub_category,
      description: product.description,
      benefits: joinByComma(product.benefits),
      thumbnail: product.thumbnail,
    },
  });

  const handleImagePreview = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUpload(file);
    setGalleryKey(null);

    const previewURL = URL.createObjectURL(file);
    setPreview(previewURL);
  };

  const handleSelectFromGallery = (key: string) => {
    setGalleryKey(key);
    setUpload(null);

    const previewUrl = `${IMAGE_CDN}/${key}`;
    setPreview(previewUrl);
  };

  const onFormSubmit = async (data: EditProductFormData) => {
    const dirtyFields = form.formState.dirtyFields;

    const updatedData: Partial<EditProductFormData> = {};

    Object.keys(dirtyFields).forEach((key) => {
      updatedData[key as keyof EditProductFormData] =
        data[key as keyof EditProductFormData];
    });

    const updatedApiData = mapPartialToUpdateDTO(updatedData);

    try {
      let thumbnailKey: string | null = null;

      // Case 1: User uploaded new image
      if (upload) {
        thumbnailKey = await uploadProductThumbnail(upload);
        if (!thumbnailKey) {
          toast.error("Failed to upload thumbnail");
          return;
        }

        updatedApiData.thumbnail = thumbnailKey;
      }

      // Case 2: User selected from image gallery
      else if (galleryKey) {
        updatedApiData.thumbnail = galleryKey;
      }

      // Prevent empty update
      if (Object.keys(updatedApiData).length === 0) {
        toast.info("No changes made");
        return;
      }

      // Validating payload
      const safePayload = updateProductSchema.partial().parse(updatedApiData);

      // Mutation call
      updateProductMutation({
        id: product.id,
        payload: safePayload,
      });

      close();
    } catch (error) {
      toast.error("Failed to update Producct!");
      console.error(error, "update product error");
    }
  };

  // const handleArchive = (id: string) => {
  //   toast.info("Product Archived");
  //   close();
  // };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)}>
        <div className="p-8 space-y-8">
          <div className="grid grid-cols-3 gap-8">
            {/* Visual Editor */}
            <div className="space-y-4 col-span-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                Product Media
              </label>
              <div className="relative group w-full aspect-square bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200 p-6 overflow-hidden flex items-center justify-center">
                <img
                  src={preview ? preview : `${IMAGE_CDN}/${product.thumbnail}`}
                  className="w-full h-full object-contain"
                  alt="Current"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center gap-3 rounded-[2rem] transition-all duration-300">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-[10px] bg-white px-5 py-2.5 rounded-xl font-black uppercase tracking-widest shadow-xl hover:scale-105 active:scale-95 transition-all"
                  >
                    Upload File
                  </button>
                  <ImageGalleryModal selectImage={handleSelectFromGallery}>
                    <button
                      type="button"
                      className="text-[10px] bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-black uppercase tracking-widest shadow-xl hover:scale-105 active:scale-95 transition-all"
                    >
                      Media Library
                    </button>
                  </ImageGalleryModal>
                </div>
              </div>
              <p className="text-[9px] text-center text-slate-400 font-medium">
                Supported formats: PNG, JPG, WebP. Max 5MB.
              </p>
            </div>

            {/* Information Editor */}
            <div className="space-y-6 col-span-2">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                        Product Label
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Product Label"
                          type="text"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                        Price
                      </FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="0" type="text" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                        Category
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Product Category"
                          type="text"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sub_category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                        Sub Category
                      </FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <SelectTrigger className="text-[10px] font-black uppercase tracking-widest focus:ring-emerald-500 rounded-2xl p-4 focus:ring-4 focus:ring-emerald-500/5 border-2 border-slate-100 focus:border-emerald-500 h-14">
                            <SelectValue placeholder="Select Sub-Category" />
                          </SelectTrigger>
                          <SelectContent>
                            {SubCategory.map((status) => (
                              <SelectItem
                                value={status}
                                key={status}
                                className="focus:bg-emerald-500"
                              >
                                {status}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <FormField
                  control={form.control}
                  name="benefits"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                        Benefits
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter Benefits with comma saperated"
                          type="text"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                  Comprehensive Description
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    className="w-full border-2 border-slate-100 p-5 rounded-[2rem] h-40 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/5 outline-none transition-all font-medium leading-relaxed"
                    placeholder="Tell customers about the benefits and ingredients..."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={(e) => handleImagePreview(e)}
          />

          <div className="flex flex-col md:flex-row gap-4 pt-6">
            {/* <button
              type="button"
              onClick={() => handleArchive(product.id)}
              className="flex-1 text-rose-500 font-black text-xs uppercase tracking-[0.2em] hover:bg-rose-50 py-5 rounded-2xl transition-all hover:border-rose-100 border-red-500 border"
            >
              Archive Product
            </button> */}
            <button
              disabled={isPending}
              type="submit"
              className="flex-[2] bg-emerald-800 hover:bg-emerald-900 text-white py-5 rounded-2xl font-black text-sm uppercase tracking-[0.3em] shadow-2xl shadow-slate-900/20 active:scale-95 transition-all"
            >
              {isPending ? "Updating..." : "Update Detail"}
            </button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default EditProductForm;
